/**
 * Composant pour tester l'envoi de SMS
 */
import React, { useState, useEffect, useRef } from 'react';
import { servicesAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import './SMSTestPanel.css';

/**
 * Interface pour les crédits SMS
 */
interface SMSCredits {
  credits: number;
  success?: boolean;
}

/**
 * Interface pour les événements Socket.IO
 */
interface SMSEvent {
  type: 'test' | 'reminder';
  success: boolean;
  credits: number;
  timestamp: string;
  appointmentId?: number;
  status?: string;
}

/**
 * Composant pour tester l'envoi de SMS
 */
const SMSTestPanel: React.FC = () => {
  // États pour le formulaire
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [message, setMessage] = useState<string>('Ceci est un message de test de HOSPILink.');
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPanel, setShowPanel] = useState<boolean>(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{ checked: boolean; connected: boolean; message: string }>({
    checked: false,
    connected: false,
    message: '',
  });
  
  // Référence pour la connexion Socket.IO
  const socketRef = useRef<Socket | null>(null);
  const [lastSMSEvent, setLastSMSEvent] = useState<SMSEvent | null>(null);

  /**
   * Initialiser la connexion Socket.IO
   */
  useEffect(() => {
    if (showPanel && !socketRef.current) {
      // Connexion au serveur Socket.IO
      const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
      console.log('Socket URL utilisée:', SOCKET_URL); // Pour déboguer
      
      // Ajouter des options pour améliorer la connexion
      socketRef.current = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
        forceNew: true
      });
      
      // Écouter les événements
      socketRef.current.on('connect', () => {
        console.log('Connecté au serveur Socket.IO');
      });
      
      socketRef.current.on('disconnect', () => {
        console.log('Déconnecté du serveur Socket.IO');
      });
      
      // Écouter les événements d'envoi de SMS
      socketRef.current.on('sms:sent', (data: SMSEvent) => {
        console.log('Événement SMS reçu:', data);
        setLastSMSEvent(data);
        
        // Mettre à jour les crédits
        if (data.credits) {
          setCredits(data.credits);
        }
        
        // Si c'est un SMS de test et que nous avons un résultat en cours, mettre à jour le résultat
        if (data.type === 'test' && result) {
          setResult((prev: any) => ({
            ...prev,
            credits: data.credits
          }));
        }
      });
      
      // Écouter les événements de mise à jour de statut SMS (via webhook)
      socketRef.current.on('sms:status-updated', (data: any) => {
        console.log('Événement de mise à jour de statut SMS reçu:', data);
        
        // Mettre à jour le dernier événement SMS si c'est pour le même rendez-vous
        if (lastSMSEvent && lastSMSEvent.appointmentId === data.appointmentId) {
          setLastSMSEvent(prev => prev ? {
            ...prev,
            status: data.status,
            timestamp: data.timestamp
          } : null);
        }
        
        // Notifier l'utilisateur avec un toast ou une alerte
        // (cette partie est optionnelle, vous pouvez l'implémenter si vous le souhaitez)
      });
    }
    
    // Nettoyer la connexion Socket.IO lors du démontage du composant
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [showPanel, result]);

  /**
   * Vérifier la connexion à l'API SMSFactor et récupérer les crédits
   */
  const checkConnection = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Vérifier la connexion
      console.log('Vérification de la connexion à l\'API SMSFactor...');
      const response = await servicesAPI.verifySMSConnection();
      
      setConnectionStatus({
        checked: true,
        connected: response.success,
        message: response.message || (response.success ? 'Connexion établie' : 'Connexion échouée'),
      });
      
      // Si la connexion est établie, récupérer les crédits
      if (response.success) {
        try {
          console.log('Récupération des crédits SMS...');
          const creditsResponse = await servicesAPI.getSMSCredits();
          if (creditsResponse && typeof creditsResponse.credits === 'number') {
            setCredits(creditsResponse.credits);
            console.log(`Crédits SMS disponibles: ${creditsResponse.credits}`);
          }
        } catch (creditsError) {
          console.error('Erreur lors de la récupération des crédits SMS:', creditsError);
          // Ne pas bloquer le flux principal si la récupération des crédits échoue
        }
      }
    } catch (err) {
      console.error('Erreur lors de la vérification de la connexion:', err);
      setConnectionStatus({
        checked: true,
        connected: false,
        message: err instanceof Error ? err.message : 'Erreur inconnue lors de la vérification',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Envoyer un SMS de test
   */
  const sendTestSMS = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phoneNumber) {
      setError('Veuillez saisir un numéro de téléphone');
      return;
    }
    
    // Vérifier le format du numéro de téléphone (validation basique)
    const phoneRegex = /^(\+33|0)[1-9](\d{2}){4}$/;
    const cleanedPhone = phoneNumber.replace(/\s/g, '');
    if (!phoneRegex.test(cleanedPhone)) {
      setError('Format de numéro de téléphone invalide. Utilisez un format français (ex: 06 12 34 56 78 ou +33 6 12 34 56 78)');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      console.log(`Envoi d'un SMS de test au numéro: ${phoneNumber}`);
      const response = await servicesAPI.sendTestSMS(phoneNumber, message);
      console.log('Réponse du serveur:', response);
      setResult(response);
      
      // Les crédits seront mis à jour automatiquement via Socket.IO
    } catch (err) {
      console.error('Erreur lors de l\'envoi du SMS:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'envoi du SMS');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Afficher ou masquer le panneau de test
   */
  const togglePanel = () => {
    setShowPanel(!showPanel);
    if (!showPanel && !connectionStatus.checked) {
      checkConnection();
    }
  };
  
  // Vérifier la connexion au chargement du composant
  useEffect(() => {
    if (showPanel && !connectionStatus.checked) {
      checkConnection();
    }
  }, [showPanel]);

  return (
    <div className="sms-test-container">
      <button 
        className="btn btn-secondary toggle-btn" 
        onClick={togglePanel}
      >
        {showPanel ? 'Masquer le panneau de test SMS' : 'Tester l\'envoi de SMS'}
      </button>
      
      {showPanel && (
        <div className="sms-test-panel">
          <h3>Test d'envoi de SMS</h3>
          
          <div className="connection-status">
            <p>
              Statut de connexion: 
              {connectionStatus.checked ? (
                connectionStatus.connected ? (
                  <span className="status-connected"> Connecté</span>
                ) : (
                  <span className="status-error"> Non connecté</span>
                )
              ) : (
                <span className="status-checking"> Vérification...</span>
              )}
              
              {credits !== null && connectionStatus.connected && (
                <span className="credits-info"> (Crédits disponibles: {credits})</span>
              )}
            </p>
            
            {connectionStatus.message && (
              <p className="status-message">{connectionStatus.message}</p>
            )}
            
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={checkConnection} 
              disabled={loading}
            >
              {loading && !result ? 'Vérification...' : 'Vérifier la connexion'}
            </button>
          </div>
          
          {lastSMSEvent && (
            <div className="last-sms-event">
              <p>
                <strong>Dernier événement SMS:</strong> {lastSMSEvent.type === 'test' ? 'Test' : 'Rappel'} 
                {' '}envoyé à {new Date(lastSMSEvent.timestamp).toLocaleTimeString()}
              </p>
            </div>
          )}
          
          <form onSubmit={sendTestSMS} className="sms-test-form">
            <div className="form-group">
              <label htmlFor="phoneNumber">Numéro de téléphone:</label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Ex: 06 12 34 56 78"
                className="form-control"
                required
                disabled={loading}
              />
              <small className="form-text">Format: numéro français avec ou sans indicatif (+33)</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message:</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="form-control"
                rows={3}
                disabled={loading}
              />
              <small className="form-text">Longueur: {message.length} caractères</small>
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={loading || !connectionStatus.connected || !phoneNumber}
            >
              {loading ? 'Envoi en cours...' : 'Envoyer un SMS de test'}
            </button>
          </form>
          
          {error && (
            <div className="error-message">
              <p><strong>Erreur:</strong> {error}</p>
              <button 
                className="btn btn-outline-secondary btn-sm" 
                onClick={() => setError(null)}
              >
                Fermer
              </button>
            </div>
          )}
          
          {result && (
            <div className="result-container">
              <h4>Résultat de l'envoi</h4>
              <div className="result-status">
                <span className={result.success ? "status-success" : "status-error"}>
                  {result.success ? "✓ Succès" : "✗ Échec"}
                </span>
                {result.message && <p>{result.message}</p>}
              </div>
              <pre>{JSON.stringify(result, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SMSTestPanel;
